import {
  AssistActivationOutcome,
  CLIENT_SYSTEM_CONFIGURATION_REFUSALS,
  ClientSystemConfigurationRefusal,
  ClientSystemConfigurationValue,
} from "@/data/models/ClientAdministrationRow";
import { MIRA_MODES, MiraMode } from "@/data/models/MiraMode";
import { CHAT_MODE_VALUES, ChatMode, chatFlagsFromMode, chatModeFromFlags } from "@/utils/ChatMode";

/**
 * The Systemkonfiguration logic (#2610141) shared by the IRES Admin Client's Systemkonfiguration card, its
 * dialog and the Verlauf: the rows in IRES Office's order and wording, the choices that pair two stored
 * fields (Chat, ASSIST) and Mira's three values. Pure, and a leaf like `ClientProjectOrganisation.ts`.
 * IRES Cloud's `setClientSystemConfigurationForAdministration` is the authority.
 */

type Translate = (key: string, values?: unknown[]) => string;

/** The switches shown as Ja / Nein, each with IRES Office's label. */
type Switch = Exclude<keyof ClientSystemConfigurationValue, "renoActivationScope" | "miraMode" | "enablePersonalChat" | "enableProjectChat" | "enableAiPredictions">;

/** One row of the card and the dialog: a Ja / Nein switch, or one of the three choices. */
export type SystemConfigurationRow =
  | { kind: "switch"; field: Switch; labelKey: string }
  | { kind: "chat"; labelKey: string }
  | { kind: "assist"; labelKey: string }
  | { kind: "mira"; labelKey: string };

/** The rows in the order and wording of IRES Office's Einstellungen > Systemkonfiguration, Mira last. */
export const SYSTEM_CONFIGURATION_ROWS: SystemConfigurationRow[] = [
  { kind: "switch", field: "enableGeoEncoding", labelKey: "settings.system.geoEncoding" },
  { kind: "switch", field: "enableTracking", labelKey: "settings.system.tracking" },
  { kind: "switch", field: "enableVehicleManagement", labelKey: "settings.vehicleManagement.title" },
  { kind: "switch", field: "enableScheduling", labelKey: "settings.system.operationalPlaning" },
  { kind: "switch", field: "enableAllDevicesManagement", labelKey: "settings.allDevicesManagement.title" },
  { kind: "switch", field: "enableDeviceManagementTile", labelKey: "settings.showDeviceManagementInCockpit.title" },
  { kind: "switch", field: "enableTimeTracking", labelKey: "settings.timeTracking.title" },
  { kind: "chat", labelKey: "settings.system.chat.title" },
  { kind: "switch", field: "enableDeviceCheckManagement", labelKey: "settings.deviceCheckManagement.title" },
  { kind: "assist", labelKey: "settings.aiPredictions.title" },
  { kind: "switch", field: "enableBillingCatalogs", labelKey: "settings.billingCatalogs.title" },
  { kind: "mira", labelKey: "settings.system.mira.title" },
];

/** Every stored field, for comparing two sets of values. */
const FIELDS: Array<keyof ClientSystemConfigurationValue> = [
  "enableGeoEncoding",
  "enableTracking",
  "enableVehicleManagement",
  "enableScheduling",
  "enableAllDevicesManagement",
  "enableDeviceManagementTile",
  "enableTimeTracking",
  "enablePersonalChat",
  "enableProjectChat",
  "enableDeviceCheckManagement",
  "enableAiPredictions",
  "enableBillingCatalogs",
  "renoActivationScope",
  "miraMode",
];

/** ASSIST as IRES Office's picker offers it: on with either activation scope, or off. */
export const ASSIST_MODES = ["ALL_DRYINGS", "ACTIVE_AND_NEW_ONLY", "OFF"] as const;
export type AssistMode = typeof ASSIST_MODES[number];

/** Mira's three values in the order the picker offers them. */
export const MIRA_MODE_VALUES: readonly MiraMode[] = MIRA_MODES;

/** The Chat choice of a set of values. */
export function chatModeOf(value: ClientSystemConfigurationValue): ChatMode {
  return chatModeFromFlags(value.enablePersonalChat, value.enableProjectChat);
}

/** Sets the Chat choice: its two stored flags. */
export function setChatMode(value: ClientSystemConfigurationValue, mode: ChatMode): void {
  Object.assign(value, chatFlagsFromMode(mode));
}

/** The ASSIST choice of a set of values: off, or on with its scope. */
export function assistModeOf(value: ClientSystemConfigurationValue): AssistMode {
  return value.enableAiPredictions ? value.renoActivationScope : "OFF";
}

/** Sets the ASSIST choice; off leaves the scope as it was, as IRES Office's page does. */
export function setAssistMode(value: ClientSystemConfigurationValue, mode: AssistMode): void {
  value.enableAiPredictions = mode !== "OFF";
  if (mode !== "OFF") {
    value.renoActivationScope = mode;
  }
}

/** The values a row shows. */
export function rowText(row: SystemConfigurationRow, value: ClientSystemConfigurationValue, t: Translate): string {
  switch (row.kind) {
    case "switch":
      return t(value[row.field] ? "common.yes" : "common.no");
    case "chat":
      return t(`settings.system.chat.mode.${chatModeOf(value)}`);
    case "assist":
      return t(`settings.aiPredictions.mode.${assistModeOf(value)}`);
    case "mira":
      return t(`settings.system.mira.mode.${value.miraMode}`);
  }
}

/** The choices a picker row offers: the stored values for Ja / Nein, the modes for the three choices. */
export function rowOptions(row: SystemConfigurationRow): Array<boolean | string> {
  switch (row.kind) {
    case "switch":
      return [true, false];
    case "chat":
      return [...CHAT_MODE_VALUES];
    case "assist":
      return [...ASSIST_MODES];
    case "mira":
      return [...MIRA_MODE_VALUES];
  }
}

/** A copy, so the dialog's form never shares an object with the loaded value. */
export function copySystemConfiguration(value: ClientSystemConfigurationValue): ClientSystemConfigurationValue {
  return { ...value };
}

/** Whether two sets of values differ in any stored field. */
export function systemConfigurationDiffers(left: ClientSystemConfigurationValue, right: ClientSystemConfigurationValue): boolean {
  return FIELDS.some((field) => left[field] !== right[field]);
}

/** Whether a switch is blocked by its prerequisite: TRACKING needs KARTE, the Cockpit tile needs Alle Geräte. */
export function isBlockedSwitch(field: Switch, value: ClientSystemConfigurationValue): boolean {
  return (field === "enableTracking" && !value.enableGeoEncoding) ||
    (field === "enableDeviceManagementTile" && !value.enableAllDevicesManagement);
}

/** Switches TRACKING and the Cockpit tile off when their prerequisite goes off, as IRES Office's page saves them. */
export function applySystemConfigurationDependencies(value: ClientSystemConfigurationValue): void {
  if (!value.enableGeoEncoding) {
    value.enableTracking = false;
  }
  if (!value.enableAllDevicesManagement) {
    value.enableDeviceManagementTile = false;
  }
}

/** Whether a save takes the devices out of their vehicles: Fahrzeuge going off. The dialog confirms it first. */
export function emptiesVehicles(stored: ClientSystemConfigurationValue, form: ClientSystemConfigurationValue): boolean {
  return stored.enableVehicleManagement && !form.enableVehicleManagement;
}

/** IRES Cloud's refusal reason in a failed call, else null. */
export function clientSystemConfigurationRefusalOf(error: unknown): ClientSystemConfigurationRefusal | null {
  const reason = (error as { details?: { reason?: unknown } } | null)?.details?.reason;
  return typeof reason === "string" && CLIENT_SYSTEM_CONFIGURATION_REFUSALS.includes(reason as ClientSystemConfigurationRefusal)
    ? (reason as ClientSystemConfigurationRefusal)
    : null;
}

/** The i18n key of a refusal from IRES Cloud, by its `details.reason`; a general failure otherwise. */
export function clientSystemConfigurationErrorKey(error: unknown): string {
  const reason = clientSystemConfigurationRefusalOf(error);
  return reason ? `admin.clients.systemConfiguration.errors.${reason}` : "admin.clients.systemConfiguration.errors.FAILED";
}

/** The label of a stored field in the Verlauf; the pair behind Chat and ASSIST each under their own name. */
export const SYSTEM_CONFIGURATION_FIELD_LABEL_KEYS: Record<keyof ClientSystemConfigurationValue, string> = {
  ...(Object.fromEntries(
    SYSTEM_CONFIGURATION_ROWS.filter((row): row is Extract<SystemConfigurationRow, { kind: "switch" }> => row.kind === "switch")
      .map((row) => [row.field, row.labelKey])
  ) as Record<Switch, string>),
  enablePersonalChat: "admin.clients.setup.functions.personalChat",
  enableProjectChat: "admin.clients.setup.functions.projectChat",
  enableAiPredictions: "settings.aiPredictions.title",
  renoActivationScope: "admin.clients.systemConfiguration.assistScope",
  miraMode: "settings.system.mira.title",
};

/** A stored field's value in the Verlauf: Ja / Nein, ASSIST's scope or Mira's value by name. */
export function systemConfigurationValueText(field: keyof ClientSystemConfigurationValue, value: unknown, t: Translate): string | null {
  if (field === "renoActivationScope" && (value === "ALL_DRYINGS" || value === "ACTIVE_AND_NEW_ONLY")) {
    return t(`settings.aiPredictions.mode.${value}`);
  }
  if (field === "miraMode" && (MIRA_MODES as readonly unknown[]).includes(value)) {
    return t(`settings.system.mira.mode.${value}`);
  }
  if (typeof value === "boolean") {
    return t(value ? "common.yes" : "common.no");
  }
  return null;
}

/** The one snackbar after a save: an error or a success, by its i18n key. */
export interface SaveFeedback {
  kind: "success" | "error";
  key: string;
}

/**
 * What to say after a saved change, the same on IRES Office's page and the IRES Admin Client's dialog: a
 * side effect that failed first, then how ASSIST's activation went (when ASSIST was switched on), else
 * the plain success.
 *
 * @param assistActivation - IRES Cloud's answer about ASSIST's activation.
 * @param effectsFailed - whether a side effect failed after the save.
 * @param savedKey - the success message when there is nothing else to say.
 */
export function systemConfigurationSaveFeedback(
  assistActivation: AssistActivationOutcome,
  effectsFailed: boolean,
  savedKey: string
): SaveFeedback {
  if (effectsFailed) {
    return { kind: "error", key: "admin.clients.systemConfiguration.effectsFailed" };
  }
  if (assistActivation && "failed" in assistActivation) {
    return { kind: "error", key: "settings.aiPredictions.activationBackfill.triggerFailed" };
  }
  switch (assistActivation?.status) {
    case "started":
      return { kind: "success", key: "settings.aiPredictions.activationBackfill.started" };
    case "already_running":
      return { kind: "success", key: "settings.aiPredictions.activationBackfill.alreadyRunning" };
    case "no_op":
      return { kind: "error", key: "settings.aiPredictions.activationBackfill.noOp" };
    default:
      return { kind: "success", key: savedKey };
  }
}
